<script lang="ts" context="module">
    export type { SortableTreeKeyValue, SortableTreeNodeData, SortableTreeOnChangeFunction, SortableTreeOnClickFunction, SortableTreeRenderLabelFunction } from 'sortable-tree';
</script>

<script lang="ts" generics="T extends SortableTreeKeyValue">
    import type { Action } from 'svelte/action';

    import type { SortableTreeKeyValue } from 'sortable-tree';
    import type { BivariantAsyncCallback, TreeComponentNode, TreeDropResult, TreeNodeData } from './utils';

    type GenericSortableTreeRenderLabelFunction<TValue extends SortableTreeKeyValue> = (data: TValue) => string;
    type GenericSortableTreeOnChangeFunction<TValue extends SortableTreeKeyValue> = BivariantAsyncCallback<[TreeDropResult<TValue>]>;
    type GenericSortableTreeOnClickFunction<TValue extends SortableTreeKeyValue> = BivariantAsyncCallback<[Event, TreeComponentNode<TValue>]>;

    export let nodes: TreeNodeData<T>[] = [
        {
            data: { title: 'Home' } as unknown as T,
            nodes: [
                { data: { title: 'Page 1' } as unknown as T, nodes: [] },
                {
                    data: { title: 'Page 2' } as unknown as T,
                    nodes: [{ data: { title: 'Subpage' } as unknown as T, nodes: [] }],
                },
            ],
        },
    ];

    export let disableSorting = false;
    export let initCollapseLevel = 3;
    export let icons = {
        collapsed: `
		<svg
		xmlns="http://www.w3.org/2000/svg" 
		width="1em" 
		height="1em" 
		fill="currentColor" 
		class="bi bi-caret-right-fill" 
		viewBox="0 0 16 16">
			<path d="m12.14 8.753-5.482 4.796c-.646.566-1.658.106-1.658-.753V3.204a1 1 0 0 1 1.659-.753l5.48 4.796a1 1 0 0 1 0 1.506z"/>
		</svg>`,
        open: `
		<svg 
		xmlns="http://www.w3.org/2000/svg" 
		width="1em" 
		height="1em" 
		fill="currentColor" 
		class="bi bi-caret-down-fill" 
		viewBox="0 0 16 16">
			<path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
		</svg>
	`,
    };

    export let renderLabel: GenericSortableTreeRenderLabelFunction<T> = data => `
        <span>
            <span>⌘ ${data.title}</span>
            <span>↕</span>
        </span>`;

    export let onChange: GenericSortableTreeOnChangeFunction<T> = async ({ nodes, movedNode, srcParentNode, targetParentNode }) => {
        console.log(nodes[0], movedNode.data, srcParentNode.data, targetParentNode.data);
    };

    export let onClick: GenericSortableTreeOnClickFunction<T> = async (event, node) => {
        console.log(node.data);
    };

    let treeEl: HTMLDivElement;

    const initializeTree = () =>
        import('sortable-tree').then(({ default: SortableTree }) => {
            if (treeEl) treeEl.innerHTML = '';
            new SortableTree({
                nodes,
                disableSorting,
                element: treeEl,
                lockRootLevel: false,
                initCollapseLevel,
                renderLabel,
                onChange,
                onClick,
                icons,
            } as unknown as ConstructorParameters<typeof SortableTree>[0]);
        });

    const mount: Action<HTMLDivElement> = node => {
        initializeTree();
        treeEl = node;
    };

    $: {
        nodes;
        initializeTree();
    }
</script>

<div bind:this={treeEl} use:mount {...$$restProps} class:tree={treeEl} />

<style global lang="postcss">
    @import 'sortable-tree/dist/sortable-tree.css';
    .tree {
        --st-label-height: 3.75rem;
        --st-subnodes-padding-left: 2.25rem;
        --st-collapse-icon-width: 1.5rem;
        --st-collapse-icon-height: 3.75rem;
        --st-collapse-icon-size: 0.9rem;
        @apply flex flex-1 flex-col pl-9;

        &__label {
            display: flex;
            padding: 4px;
            font-size: 1rem;
            font-weight: 480;
            cursor: move;

            &:hover {
                z-index: 2;
                --color: var(--hover-color);
                --background: var(--hover-background);
            }

            &:after {
                content: '';
                position: absolute;
                z-index: 4;
                inset: -2px 2px;
                border-top: 4px solid transparent;
                border-bottom: 4px solid transparent;
                transition: border-color 0.2s;
                pointer-events: none;
            }

            & > span {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 1rem;
                width: 100%;
                height: 100%;
                border: 2px solid;
                border-radius: 6px;
                color: var(--color);
                background-color: var(--background);
                transition:
                    color 0.2s,
                    background 0.2s;
            }
        }

        &__node--drop-before > &__label:after {
            @apply border-t-blue-500;
        }

        &__node--drop-after > &__label:after {
            @apply border-b-blue-500;
        }

        &__node--drop-inside > &__label {
            z-index: 3;
            @apply bg-primary-50 text-blue-700;
        }

        &__node--dragging &__label > span {
            border-style: dashed;
            cursor: move !important;
        }
    }
</style>
